import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ImagePicker from './ImagePicker';

const AdminEpisodeForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [guests, setGuests] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [formData, setFormData] = useState({
    episode_number: '',
    title: '',
    description: '',
    duration: '',
    guest_id: '',
    guest_name: '',
    guest_role: '',
    guest_photo: '',
    thumbnail: '',
    spotify_url: '',
    youtube_url: '',
    is_featured: false
  });
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    fetchGuests();
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      fetchEpisode();
    }
  }, [id, isEdit]);

  // Separate useEffect to handle guest selection after both data sets are loaded
  useEffect(() => {
    if (isEdit && guests.length > 0 && formData.guest_name && !selectedGuest) {
      console.log('Attempting to match guest:', formData.guest_name);
      console.log('Available guests:', guests.map(g => g.name));
      
      const matchingGuest = guests.find(g => g.name === formData.guest_name);
      
      if (matchingGuest) {
        console.log('Found matching guest:', matchingGuest);
        setSelectedGuest(matchingGuest);
        setFormData(prev => ({
          ...prev,
          guest_id: matchingGuest.id,
          guest_photo: prev.guest_photo || matchingGuest.image_url || matchingGuest.imageUrl || '',
          guest_role: prev.guest_role || matchingGuest.title_role || matchingGuest.titleRole || ''
        }));
      } else {
        console.log('No matching guest found for:', formData.guest_name);
      }
    }
  }, [guests, formData.guest_name, isEdit, selectedGuest]);

  const fetchGuests = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch guests');

      const data = await response.json();
      setGuests(data.items);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const fetchEpisode = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/episodes/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch episode');

      const episode = await response.json();
      
      console.log('Episode data loaded:', episode);
      setFormData({
        ...episode,
        episode_number: episode.episode_number || episode.episodeNumber || '',
        guest_name: episode.guest_name || episode.guestName || '',
        guest_role: episode.guest_role || episode.guestRole || '',
        guest_photo: episode.guest_photo || episode.guestPhoto || '',
        spotify_url: episode.spotify_url || episode.spotifyUrl || '',
        youtube_url: episode.youtube_url || episode.youtubeUrl || '',
        is_featured: episode.is_featured !== undefined ? episode.is_featured : (episode.isFeatured !== undefined ? episode.isFeatured : false)
      });
    } catch (error) {
      console.error('Error fetching episode:', error);
      showAlert('Error', 'Failed to fetch episode', () => navigate('/admin/episodes'));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'guest_id') {
      const guest = guests.find(g => g.id === value);
      if (guest) {
        setSelectedGuest(guest);
        setFormData(prev => ({
          ...prev,
          guest_id: value,
          guest_name: guest.name,
          guest_role: guest.title_role || guest.titleRole || '',
          guest_photo: guest.image_url || guest.imageUrl || ''
        }));
      } else {
        setSelectedGuest(null);
        setFormData(prev => ({
          ...prev,
          guest_id: '',
          guest_name: '',
          guest_role: '',
          guest_photo: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = isEdit 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/episodes/${id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/episodes`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      // Create episode data without guest_id (backend doesn't need it)
      const episodeData = { ...formData };
      delete episodeData.guest_id;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(episodeData)
      });

      if (!response.ok) throw new Error(`Failed to ${isEdit ? 'update' : 'create'} episode`);

      navigate('/admin/episodes');
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} episode:`, error);
      showAlert('Error', `Failed to ${isEdit ? 'update' : 'create'} episode`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow">
          {isEdit ? 'Edit Episode' : 'Create Episode'}
        </h2>
        <button
          onClick={() => navigate('/admin/episodes')}
          className="w-full sm:w-auto bg-charcoal/40 text-warm-white px-6 py-3 rounded-xl font-medium hover:bg-charcoal/60 transition-colors duration-300 text-center"
        >
          ← Back to Episodes
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Episode Number *
              </label>
              <input
                type="text"
                name="episode_number"
                value={formData.episode_number}
                onChange={handleChange}
                required
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="EP. 01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Duration *
              </label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="1h 30min"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-soft-gray mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
              placeholder="Building a $10M ARR SaaS While Battling Burnout"
            />
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-soft-gray mb-2">
              Description/Quote *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
              placeholder="Main description text for the episode"
            />
          </div>
        </div>

        {/* Guest Information */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Guest Information</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-soft-gray mb-2">
              Select Guest *
            </label>
            <select
              name="guest_id"
              value={formData.guest_id}
              onChange={handleChange}
              required
              className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
            >
              <option value="">Choose a guest...</option>
              {guests.map((guest) => (
                <option key={guest.id} value={guest.id}>
                  {guest.name} - {guest.title_role || guest.titleRole}
                </option>
              ))}
            </select>
          </div>

          {selectedGuest && (
            <div className="bg-midnight/40 rounded-xl p-4 border border-slate-dark/20">
              <div className="flex items-center gap-4">
                <img
                  src={selectedGuest.image_url || selectedGuest.imageUrl}
                  alt={selectedGuest.name}
                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                />
                <div className="flex-1 text-left">
                  <p className="text-warm-white">
                    <span className="font-medium">{selectedGuest.name}</span>
                    <span className="text-soft-gray"> - {selectedGuest.title_role || selectedGuest.titleRole}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Media */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Media</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-soft-gray mb-2">
              Thumbnail Image *
            </label>
            
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="w-full sm:w-48 h-48 rounded-xl border-2 border-dashed border-slate-dark/50 bg-midnight/40 flex flex-col items-center justify-center overflow-hidden flex-shrink-0 relative group">
                {formData.thumbnail ? (
                  <>
                    <img src={formData.thumbnail} alt="Thumbnail preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-midnight/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button 
                        type="button"
                        onClick={() => setShowImagePicker(true)}
                        className="bg-electric-yellow text-midnight px-4 py-2 rounded-lg font-bold text-sm transform transition-all duration-300 scale-95 group-hover:scale-100 shadow-lg shadow-electric-yellow/20"
                      >
                        Change Image
                      </button>
                    </div>
                  </>
                ) : (
                  <button 
                    type="button"
                    onClick={() => setShowImagePicker(true)}
                    className="flex flex-col items-center justify-center w-full h-full gap-2 text-soft-gray hover:text-electric-yellow hover:bg-white/5 transition-all duration-300"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium">Select Image</span>
                  </button>
                )}
              </div>
              <div className="flex-1 w-full">
                <input
                  type="url"
                  name="thumbnail"
                  value={formData.thumbnail}
                  onChange={handleChange}
                  required
                  className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white text-sm placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 mt-2"
                  placeholder="Selected image URL will appear here..."
                  readOnly
                />
                <p className="text-soft-gray text-sm mt-3">
                  Click the placeholder box to select an image from your Cloudflare Media Library.
                </p>
              </div>
            </div>
            
            {showImagePicker && (
              <ImagePicker 
                onSelect={(url) => {
                  setFormData(prev => ({ ...prev, thumbnail: url }));
                  setShowImagePicker(false);
                }}
                onClose={() => setShowImagePicker(false)}
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Spotify URL
              </label>
              <input
                type="url"
                name="spotify_url"
                value={formData.spotify_url}
                onChange={handleChange}
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="https://open.spotify.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                YouTube URL
              </label>
              <input
                type="url"
                name="youtube_url"
                value={formData.youtube_url}
                onChange={handleChange}
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="https://youtube.com/..."
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Status</h3>
          
          <label className="flex items-start gap-3 p-4 bg-midnight/40 rounded-xl border border-slate-dark/30">
            <input
              type="checkbox"
              name="is_featured"
              checked={formData.is_featured}
              onChange={handleChange}
              className="w-5 h-5 mt-1 rounded border-slate-dark bg-midnight/80 text-electric-yellow focus:ring-electric-yellow/50"
            />
            <div className="text-left">
              <span className="text-warm-white font-medium text-left">Is Featured</span>
              <p className="text-soft-gray text-sm text-left">Check to set this episode as the featured episode on the homepage</p>
            </div>
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? 'Saving...' : isEdit ? 'Update Episode' : 'Create Episode'}
          </button>
        </div>
      </form>

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEpisodeForm;